import React, { useCallback, useEffect, useRef, useState } from 'react';
import { responses } from '../../services/api';
import { IconButton } from '../ui/icon-button';
import { Spinner } from '../ui/spinner';
import { useHoldPullToRefresh } from '../../mobile/pullToRefreshHold';

export type AudioRecorderStatus = 'idle' | 'recording' | 'transcribing' | 'ready';

const TARGET_SAMPLE_RATE = 16000;
const SCRIPT_PROCESSOR_BUFFER_SIZE = 4096;
const WAV_MIME_TYPE = 'audio/wav';
const WAV_BITS_PER_SAMPLE = 16;
const WAV_CHANNEL_COUNT = 1;
const MAX_INT16 = 0x7fff;

type BrowserWindow = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };

const getAudioContextConstructor = (): typeof AudioContext | null => {
  const browserWindow = window as BrowserWindow;
  return browserWindow.AudioContext ?? browserWindow.webkitAudioContext ?? null;
};

const createAudioContext = (AudioContextConstructor: typeof AudioContext): AudioContext => {
  try {
    return new AudioContextConstructor({ sampleRate: TARGET_SAMPLE_RATE });
  } catch (error) {
    console.warn(
      '[AudioRecorderCore] AudioContext sample-rate hint was rejected, falling back to browser default.',
      error,
    );
    return new AudioContextConstructor();
  }
};

const mergePcmChunks = (chunks: Float32Array[]): Float32Array => {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const merged = new Float32Array(totalLength);
  let offset = 0;

  chunks.forEach((chunk) => {
    merged.set(chunk, offset);
    offset += chunk.length;
  });

  return merged;
};

const downsamplePcm = (
  samples: Float32Array,
  inputSampleRate: number,
  outputSampleRate: number,
): Float32Array => {
  if (inputSampleRate === outputSampleRate) {
    return samples;
  }

  const sampleRateRatio = inputSampleRate / outputSampleRate;
  const outputLength = Math.round(samples.length / sampleRateRatio);
  const downsampled = new Float32Array(outputLength);

  let outputOffset = 0;
  let inputOffset = 0;

  while (outputOffset < outputLength) {
    const nextInputOffset = Math.round((outputOffset + 1) * sampleRateRatio);
    let accumulated = 0;
    let sampleCount = 0;

    for (let index = inputOffset; index < nextInputOffset && index < samples.length; index += 1) {
      accumulated += samples[index];
      sampleCount += 1;
    }

    downsampled[outputOffset] = sampleCount > 0 ? accumulated / sampleCount : 0;
    outputOffset += 1;
    inputOffset = nextInputOffset;
  }

  return downsampled;
};

const writeAsciiString = (view: DataView, offset: number, value: string) => {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
};

const encodeWavBlob = (samples: Float32Array, sampleRate: number): Blob => {
  const dataLength = samples.length * 2;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);
  const bytesPerSample = WAV_BITS_PER_SAMPLE / 8;
  const byteRate = sampleRate * WAV_CHANNEL_COUNT * bytesPerSample;
  const blockAlign = WAV_CHANNEL_COUNT * bytesPerSample;

  writeAsciiString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeAsciiString(view, 8, 'WAVE');
  writeAsciiString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, WAV_CHANNEL_COUNT, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, WAV_BITS_PER_SAMPLE, true);
  writeAsciiString(view, 36, 'data');
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index]));
    view.setInt16(
      offset,
      sample < 0 ? sample * 0x8000 : sample * MAX_INT16,
      true,
    );
    offset += 2;
  }

  return new Blob([buffer], { type: WAV_MIME_TYPE });
};

export interface AudioRecorderCoreProps {
  onRecordingComplete: (blob: Blob | null, transcription?: string) => void;
  audioBlob: Blob | null;
  onError?: (error: string) => void;
  onStatusChange?: (status: AudioRecorderStatus) => void;
}

export const AudioRecorderCore: React.FC<AudioRecorderCoreProps> = ({
  onRecordingComplete,
  onError,
  audioBlob,
  onStatusChange,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(audioBlob);
  const [isTranscribing, setIsTranscribing] = useState(false);
  useHoldPullToRefresh(isStarting || isRecording || isTranscribing);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const pcmChunksRef = useRef<Float32Array[]>([]);
  const inputSampleRateRef = useRef<number>(TARGET_SAMPLE_RATE);
  const previousRecordingRef = useRef<Blob | null>(audioBlob);
  // The token owns both microphone acquisition and its eventual transcription.
  // Disposing this component invalidates asynchronous work from that capture.
  const captureRef = useRef<object | null>(null);
  const stoppingRef = useRef(false);

  useEffect(() => {
    setRecordingBlob(audioBlob);
    previousRecordingRef.current = audioBlob;
  }, [audioBlob]);

  useEffect(() => {
    const status: AudioRecorderStatus = isStarting || isRecording
      ? 'recording'
      : isTranscribing
        ? 'transcribing'
        : recordingBlob
          ? 'ready'
          : 'idle';

    onStatusChange?.(status);
  }, [isStarting, isRecording, isTranscribing, onStatusChange, recordingBlob]);

  const cleanupRecordingResources = useCallback(async () => {
    if (processorNodeRef.current) {
      processorNodeRef.current.onaudioprocess = null;
    }
    processorNodeRef.current?.disconnect();
    processorNodeRef.current = null;

    sourceNodeRef.current?.disconnect();
    sourceNodeRef.current = null;

    gainNodeRef.current?.disconnect();
    gainNodeRef.current = null;

    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;

    const audioContext = audioContextRef.current;
    audioContextRef.current = null;
    if (audioContext && audioContext.state !== 'closed') {
      await audioContext.close().catch((error) => {
        console.warn('[AudioRecorderCore] Failed to close audio context:', error);
      });
    }
  }, []);

  useEffect(() => {
    return () => {
      captureRef.current = null;
      pcmChunksRef.current = [];
      void cleanupRecordingResources();
    };
  }, [cleanupRecordingResources]);

  const startRecording = async () => {
    if (captureRef.current) {
      return;
    }
    const capture = {};
    captureRef.current = capture;
    stoppingRef.current = false;
    setIsStarting(true);
    try {
      previousRecordingRef.current = recordingBlob;
      const AudioContextConstructor = getAudioContextConstructor();
      if (!AudioContextConstructor) {
        throw new Error('AudioContext is not available in this browser.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: TARGET_SAMPLE_RATE,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      if (captureRef.current !== capture) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      mediaStreamRef.current = stream;

      const audioContext = createAudioContext(AudioContextConstructor);
      audioContextRef.current = audioContext;
      await audioContext.resume();
      if (captureRef.current !== capture) {
        return;
      }
      inputSampleRateRef.current = audioContext.sampleRate;
      pcmChunksRef.current = [];

      const sourceNode = audioContext.createMediaStreamSource(stream);
      const processorNode = audioContext.createScriptProcessor(
        SCRIPT_PROCESSOR_BUFFER_SIZE,
        1,
        1,
      );
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0;

      processorNode.onaudioprocess = (event) => {
        const samples = event.inputBuffer.getChannelData(0);
        pcmChunksRef.current.push(new Float32Array(samples));
      };

      sourceNode.connect(processorNode);
      processorNode.connect(gainNode);
      gainNode.connect(audioContext.destination);

      sourceNodeRef.current = sourceNode;
      processorNodeRef.current = processorNode;
      gainNodeRef.current = gainNode;
      setIsRecording(true);
    } catch (error) {
      if (captureRef.current !== capture) {
        return;
      }
      console.error('Error accessing microphone:', error);
      void cleanupRecordingResources();
      setIsStarting(false);
      captureRef.current = null;
      onError?.('Could not access microphone. Please ensure you have granted permission.');
    } finally {
      if (captureRef.current === capture) {
        setIsStarting(false);
      }
    }
  };

  const finalizeRecordingBlob = () => {
    const mergedSamples = mergePcmChunks(pcmChunksRef.current);
    if (mergedSamples.length === 0) {
      throw new Error('No audio was captured.');
    }

    const normalizedSamples = downsamplePcm(
      mergedSamples,
      inputSampleRateRef.current,
      TARGET_SAMPLE_RATE,
    );

    return encodeWavBlob(normalizedSamples, TARGET_SAMPLE_RATE);
  };

  const stopRecording = async () => {
    const capture = captureRef.current;
    if (!isRecording || !capture || stoppingRef.current) {
      return;
    }
    stoppingRef.current = true;

    setIsRecording(false);
    setIsTranscribing(true);
    if (processorNodeRef.current) {
      processorNodeRef.current.onaudioprocess = null;
    }

    try {
      const blob = finalizeRecordingBlob();
      setRecordingBlob(blob);
      pcmChunksRef.current = [];
      // Release the microphone immediately; transcription only needs the WAV.
      void cleanupRecordingResources();
      await transcribeAudio(blob, capture);
    } catch (error) {
      if (captureRef.current !== capture) {
        return;
      }
      console.error('[AudioRecorderCore] Recording finalization error:', error);
      onError?.('Recording error occurred. Please try again.');

      const previousRecording = previousRecordingRef.current;
      if (previousRecording) {
        setRecordingBlob(previousRecording);
      } else {
        setRecordingBlob(null);
        onRecordingComplete(null, '');
      }
    } finally {
      if (captureRef.current === capture) {
        pcmChunksRef.current = [];
        void cleanupRecordingResources();
        captureRef.current = null;
        setIsTranscribing(false);
      }
    }
  };

  const transcribeAudio = async (
    blob: Blob,
    capture: object,
  ) => {
    try {
      const response = await responses.transcribeAudio(blob);
      if (captureRef.current !== capture) {
        return;
      }

      if (!response.transcription) {
        throw new Error('No transcription received');
      }

      const transcription = response.transcription;
      onRecordingComplete(blob, transcription);
    } catch (error) {
      if (captureRef.current !== capture) {
        return;
      }
      console.error('[AudioRecorderCore] Transcription error:', error);
      onError?.('Failed to transcribe audio. Please try again or type your response.');

      const previousRecording = previousRecordingRef.current;
      if (previousRecording) {
        setRecordingBlob(previousRecording);
      } else {
        setRecordingBlob(null);
        onRecordingComplete(null, '');
      }
    }
  };

  return (
    <div className="flex items-center justify-center">
      {!isRecording && !isTranscribing && (
        <IconButton
          variant='secondary'
          className='h-10 w-10 rounded-full border border-border bg-background/90 p-0 shadow-sm hover:bg-muted'
          icon='mic'
          iconSize={18}
          onClick={startRecording}
          disabled={isStarting}
          aria-label="Record audio"
        />
      )}

      {isRecording && (
        <IconButton
          icon='stop'
          iconSize={18}
          onClick={stopRecording}
          variant='secondary'
          className='h-10 w-10 rounded-full border border-border bg-background/90 p-0 shadow-sm hover:bg-muted'
          aria-label="Stop recording"
        />
      )}

      {isTranscribing && (
        <div
          className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/90 shadow-sm"
          aria-label="Transcribing audio"
        >
          <Spinner size="sm"/>
        </div>
      )}
    </div>
  );
};
